import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, isApiError } from '@/lib/api';

/** Limit fields returned with a panel contract. */
export interface PanelContractLimits {
  consultationCapSen: number | null;
  testsCapSen: number | null;
  proceduresCapSen: number | null;
  annualPatientLimitSen: number | null;
  warningThresholdPercent: number;
}

export interface PanelContract extends PanelContractLimits {
  id: string;
  panelId: string;
  panelName: string;
  updatedAt: string;
}

/** Backwards-compatible domain name used by the panel-limit hooks. */
export type PanelLimitConfiguration = PanelContract;

export interface UpdatePanelLimitInput extends PanelContractLimits {
  id: string;
}

export type PanelLimitStatus = 'ok' | 'near' | 'over';

export type PanelLimitCategory = 'consultation' | 'test' | 'procedure';

export interface PanelLimitDraftBillLine {
  category: PanelLimitCategory;
  amountSen: number;
}

export interface PanelLimitEvaluationInput {
  panelId: string;
  patientId: string;
  draftBillLines: PanelLimitDraftBillLine[];
}

export interface PanelLimitCategoryEvaluation {
  amountSen: number;
  capSen: number | null;
  remainingSen: number | null;
  status: PanelLimitStatus;
}

export interface PanelLimitEvaluation {
  panelId: string;
  patientId: string;
  draftBillTotalSen: number;
  annualUsedSen: number;
  projectedAnnualUsedSen: number;
  remainingAnnualSen: number | null;
  categories: Record<PanelLimitCategory, PanelLimitCategoryEvaluation>;
  annualStatus: PanelLimitStatus;
  status: PanelLimitStatus;
}

export interface PatientPanelUsageEntry {
  id: string;
  patientId: string;
  patientName: string;
  panelId: string;
  panelName: string;
  claimId: string;
  claimReference: string;
  visitDate: string;
  claimAmountSen: number;
  annualUsedSen: number;
  annualCapSen: number | null;
  remainingAnnualSen: number | null;
  warningThresholdPercent: number;
  status: PanelLimitStatus;
}

export interface PatientPanelUsageFilters {
  patientId?: string;
  panelId?: string;
  year?: number;
}

export type PanelLimitErrorKey =
  'invalidLimits' | 'invalidEvaluation' | 'notFound';

/** Maps the API's stable problem codes to user-facing translation keys. */
export function panelLimitErrorKey(error: unknown): PanelLimitErrorKey | null {
  if (!isApiError(error)) return null;
  switch (error.code) {
    case 'PANEL_LIMITS_INVALID':
      return 'invalidLimits';
    case 'PANEL_LIMIT_EVALUATION_INVALID':
      return 'invalidEvaluation';
    case 'PANEL_LIMIT_NOT_FOUND':
      return 'notFound';
    default:
      return null;
  }
}

export const panelLimitKeys = {
  all: ['panel-limits'] as const,
  configurations: ['panel-limits', 'configurations'] as const,
  usage: (filters: PatientPanelUsageFilters = {}) =>
    ['panel-limits', 'usage', filters] as const,
};

export function usePanelLimitConfigurations() {
  return useQuery({
    queryKey: panelLimitKeys.configurations,
    queryFn: ({ signal }) =>
      api
        .get<PanelLimitConfiguration[]>('/finance/panel-limits', { signal })
        .then((response) => response.data),
  });
}

export function useUpdatePanelLimitConfiguration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdatePanelLimitInput) =>
      api
        .patch<PanelLimitConfiguration>(`/finance/panel-limits/${id}`, input)
        .then((response) => response.data),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: panelLimitKeys.all }),
  });
}

export function usePatientPanelUsageLedger(
  filters: PatientPanelUsageFilters = {},
) {
  return useQuery({
    queryKey: panelLimitKeys.usage(filters),
    queryFn: ({ signal }) =>
      api
        .get<PatientPanelUsageEntry[]>('/finance/panel-limits/usage', {
          signal,
          query: {
            patientId: filters.patientId,
            panelId: filters.panelId,
            year: filters.year,
          },
        })
        .then((response) => response.data),
  });
}

export function useEvaluatePanelLimit() {
  return useMutation({
    mutationFn: (input: PanelLimitEvaluationInput) =>
      api
        .post<PanelLimitEvaluation>('/finance/panel-limits/evaluate', input)
        .then((response) => response.data),
  });
}
